<template>
  <v-container fluid class="pa-6">
    <h1 class="text-h4 font-weight-bold mb-6">Журнал входов</h1>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-4">{{ error }}</v-alert>

    <!-- Filters: the same fields the journal keeps -->
    <v-row dense class="mb-2">
      <v-col cols="12" md="3">
        <v-select v-model="filters.event" :items="eventItems" label="Событие" variant="outlined" density="compact"
                  multiple chips closable-chips clearable hide-details @update:model-value="reload"></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field v-model="filters.email" label="Почта" variant="outlined" density="compact" clearable hide-details
                      prepend-inner-icon="mdi-email-outline" @update:model-value="reloadSoon"></v-text-field>
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field v-model="filters.ip" label="IP-адрес" variant="outlined" density="compact" clearable hide-details
                      @update:model-value="reloadSoon"></v-text-field>
      </v-col>
      <v-col cols="6" md="2">
        <v-text-field v-model="filters.date_from" type="date" label="С" variant="outlined" density="compact" hide-details
                      clearable @update:model-value="reload"></v-text-field>
      </v-col>
      <v-col cols="6" md="2">
        <v-text-field v-model="filters.date_to" type="date" label="По" variant="outlined" density="compact" hide-details
                      clearable @update:model-value="reload"></v-text-field>
      </v-col>
    </v-row>
    <div v-if="filters.user_id" class="mb-2">
      <v-chip closable color="primary" variant="tonal" prepend-icon="mdi-filter" @click:close="clearUser">
        Только пользователь: {{ userLabel }}
      </v-chip>
    </div>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table-server
          :headers="headers"
          :items="items"
          :items-length="total"
          :loading="isLoading"
          :items-per-page="itemsPerPage"
          :page="page"
          item-value="id"
          hover
          fixed-header
          height="calc(100vh - 300px)"
          :items-per-page-options="[50, 100, 200]"
          @update:options="onTableOptions"
        >
          <template #[`item.created_at`]="{ item }">{{ formatTime(item.created_at) }}</template>
          <template #[`item.event`]="{ item }">
            <v-chip size="small" :color="eventColor(item.event)" variant="tonal">{{ eventLabel(item.event) }}</v-chip>
          </template>
          <template #[`item.who`]="{ item }">
            <div>{{ item.user_email || item.email || '—' }}</div>
            <div v-if="item.event.startsWith('view_as') && item.email" class="text-caption text-disabled">
              смотрит как: {{ item.email }}
            </div>
            <div v-else-if="item.user_email && item.email && item.user_email !== item.email" class="text-caption text-disabled">
              введено: {{ item.email }}
            </div>
          </template>
          <template #[`item.ip`]="{ item }">
            <a v-if="item.ip" href="#" class="text-primary text-decoration-none" @click.prevent="byIp(item.ip)">{{ item.ip }}</a>
            <span v-else>—</span>
          </template>
          <template #[`item.device`]="{ item }">
            <span :title="item.user_agent || ''">{{ item.device || '—' }}</span>
          </template>
          <template #[`item.reason`]="{ item }">{{ reasonLabel(item.reason) }}</template>
        </v-data-table-server>
      </v-card-text>
    </v-card>
    <div class="text-caption text-disabled mt-2">Записи хранятся 90 дней (политика обработки персональных данных, п. 1.4).</div>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'

const EVENTS = {
  login_success: { text: 'Вход', color: 'success' },
  login_failed: { text: 'Неудачный вход', color: 'error' },
  logout: { text: 'Выход', color: 'grey' },
  session_expired: { text: 'Сессия истекла', color: 'grey' },
  session_ended: { text: 'Сессия завершена админом', color: 'warning' },
  register_success: { text: 'Регистрация', color: 'info' },
  register_failed: { text: 'Неудачная регистрация', color: 'error' },
  password_reset_requested: { text: 'Запрос сброса пароля', color: 'warning' },
  password_reset_done: { text: 'Пароль сменён', color: 'info' },
  password_reset_failed: { text: 'Неудачная смена пароля', color: 'error' },
  user_deleted: { text: 'Пользователь удалён', color: 'error' },
  // «Просмотр как пользователь»: the admin, and in the second line the user he viewed
  view_as_started: { text: 'Начало просмотра как пользователь', color: 'warning' },
  view_as_ended: { text: 'Конец просмотра', color: 'grey' }
}
const REASONS = {
  wrong_password: 'неверный пароль',
  unknown_user: 'нет такого пользователя',
  inactive: 'учётная запись отключена',
  unconfirmed: 'почта не подтверждена',
  rate_limited: 'превышен лимит попыток',
  email_taken: 'почта уже занята',
  invite_invalid: 'неверное приглашение',
  weak_password: 'слабый пароль',
  bad_token: 'неверная или устаревшая ссылка',
  idle: 'бездействие',
  other: 'другое'
}

export default {
  name: 'AuthEvents',
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      itemsPerPage: 50,
      isLoading: false,
      error: '',
      timer: null,
      filters: { event: [], email: '', ip: '', date_from: '', date_to: '', user_id: this.$route.query.user_id || '' },
      headers: [
        { title: 'Время', key: 'created_at', sortable: false, width: 170 },
        { title: 'Событие', key: 'event', sortable: false, width: 200 },
        { title: 'Пользователь', key: 'who', sortable: false },
        { title: 'IP-адрес', key: 'ip', sortable: false, width: 150 },
        { title: 'Устройство', key: 'device', sortable: false },
        { title: 'Причина', key: 'reason', sortable: false }
      ]
    }
  },
  computed: {
    // the filter by user (link «Журнал входов пользователя» of the sessions): his email
    userLabel() {
      const id = Number(this.filters.user_id)
      const found = this.items.find(i => i.user_id === id && i.user_email)
      return this.$route.query.user_email || (found && found.user_email) || `ID ${id}`
    },
    eventItems() {
      return Object.entries(EVENTS).map(([value, e]) => ({ value, title: e.text }))
    }
  },
  methods: {
    eventLabel: e => (EVENTS[e] || { text: e }).text,
    eventColor: e => (EVENTS[e] || { color: 'grey' }).color,
    reasonLabel(r) {
      if (!r) return ''
      if (r.startsWith('admin:')) return `администратор #${r.slice(6)}`
      if (r.startsWith('user:')) return `пользователь #${r.slice(5)}`
      return REASONS[r] || r
    },
    formatTime(value) {
      return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' })
    },
    onTableOptions({ page, itemsPerPage }) {
      this.page = page
      this.itemsPerPage = itemsPerPage
      this.load()
    },
    reload() {
      this.page = 1
      this.load()
    },
    reloadSoon() {
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reload, 400)
    },
    byIp(ip) {
      this.filters.ip = ip
      this.reload()
    },
    clearUser() {
      this.filters.user_id = ''
      this.$router.replace({ query: {} })
      this.reload()
    },
    async load() {
      this.isLoading = true
      this.error = ''
      const f = this.filters
      const params = { limit: this.itemsPerPage, offset: (this.page - 1) * this.itemsPerPage }
      if (f.event && f.event.length) params.event = f.event.join(',')
      for (const key of ['email', 'ip', 'date_from', 'date_to', 'user_id']) {
        if (f[key]) params[key] = f[key]
      }
      try {
        const resp = await apiClient.get('/admin/auth-events', { params })
        this.items = resp.data.items
        this.total = resp.data.total
      } catch (e) {
        this.error = 'Ошибка загрузки журнала: ' + (e.response?.data?.detail || e.message)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>
